import { useAuth } from '@clerk/tanstack-react-start';
import { ConvexProviderWithAuth, type ConvexReactClient } from 'convex/react';
import { useCallback, useMemo, type ReactNode } from 'react';

/**
 * Same behaviour as Convex's ConvexProviderWithClerk, but uses
 * ConvexProviderWithAuth from `convex/react` so it shares one module instance
 * with useConvexAuth / useQuery (avoids the "two instances of convex/react" error).
 */
export function ConvexClerkProvider({
  client,
  children,
}: {
  client: ConvexReactClient;
  children: ReactNode;
}) {
  return (
    <ConvexProviderWithAuth client={client} useAuth={useAuthFromClerk}>
      {children}
    </ConvexProviderWithAuth>
  );
}

function useAuthFromClerk() {
  const { isLoaded, isSignedIn, getToken, orgId, orgRole, sessionId, sessionClaims } =
    useAuth();

  const fetchAccessToken = useCallback(
    async ({ forceRefreshToken }: { forceRefreshToken: boolean }) => {
      try {
        if (sessionClaims?.aud === 'convex') {
          return await getToken({ skipCache: forceRefreshToken });
        }
        return await getToken({
          template: 'convex',
          skipCache: forceRefreshToken,
        });
      } catch {
        return null;
      }
    },
    // Match official ConvexProviderWithClerk: rebuild only when auth context
    // identity changes. Including sessionClaims/getToken remounts Convex auth
    // on every JWT refresh (~5s) and makes the UI look like it is reloading.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional
    [orgId, orgRole, sessionId]
  );

  return useMemo(
    () => ({
      isLoading: !isLoaded,
      isAuthenticated: isSignedIn ?? false,
      fetchAccessToken,
    }),
    [isLoaded, isSignedIn, fetchAccessToken]
  );
}
